import { ErrorBanner, Loading, PageHeader } from '@my/ui';
import { useState } from 'react';
import { Navigate, Outlet, useLocation } from 'react-router';
import type { AccountContext } from './useAccountContext';
import { useCurrentAccount } from './useCurrentAccount';

/**
 * Title and description per section, keyed by path.
 *
 * <p>
 * The header names the SECTION, not the account, exactly as every app names the
 * screen you are on. Which account it describes is answered twice already — the
 * sidebar header and the support strip — and a third copy in the page title
 * would crowd out the one thing a title is for.
 * </p>
 */
const SECTIONS: Record<string, { title: string; description: string }> = {
  '/account': {
    title: 'Account',
    description: "Your company's name, domain and key dates.",
  },
  '/subscriptions': {
    title: 'Subscriptions',
    description: 'The apps this account can use, and the plans they are on.',
  },
  '/organisations': {
    title: 'Organisations',
    description: 'Operating companies in this account and the settings every app reads.',
  },
  '/users': {
    title: 'Users',
    description: 'People who belong to this account.',
  },
};

/**
 * Wraps the four account sections: resolves the account once and owns the
 * states they all share.
 *
 * <p>
 * The sections take no account id from the URL, deliberately. A customer
 * managing their own account has no id to put there — their URL is
 * <c>/subscriptions</c> — and building the staff version around a path
 * parameter would mean two different route shapes for one screen.
 * </p>
 */
export function AccountSectionLayout() {
  const { account, accountId, isLoading, isReady } = useCurrentAccount();
  const { pathname } = useLocation();
  const section = SECTIONS[pathname];

  // A ref callback held in state, so the first render that has the node also
  // re-renders the children with it. A plain ref would still be null when the
  // Outlet first asks for it.
  const [headerSlot, setHeaderSlot] = useState<HTMLElement | null>(null);

  if (isLoading) return <Loading label="Account" />;

  // Nothing chosen yet: the picker is the whole point of the landing screen, so
  // send them there rather than rendering four empty sections.
  if (!accountId) return <Navigate to="/" replace />;

  if (!account) {
    return (
      <div className="flex h-full flex-col px-2 pt-3">
        <ErrorBanner
          title="Account not found"
          message="That account no longer exists, or you do not have access to it."
        />
      </div>
    );
  }

  return (
    <div className="flex h-full flex-col px-2">
      <PageHeader title={section?.title ?? account.name} description={section?.description}>
        {/*
          PageHeader aligns its action area to the TOP of a title-plus-
          description block, and wraps children in its own div — so `self-*`
          here lands INSIDE that wrapper and changes nothing. Giving the slot
          the height of the title block instead, and centring within it, puts
          the button beside the heading rather than above it, without changing
          PageHeader for the other five apps.
        */}
        <div ref={setHeaderSlot} className="flex h-12 items-center gap-2" />
      </PageHeader>

      <main className="flex min-h-0 flex-1 flex-col gap-3 overflow-auto pb-4">
        <Outlet context={{ account, isReady, headerSlot } satisfies AccountContext} />
      </main>
    </div>
  );
}
